/**
 * `gamification-core` — proyección del `activity` de comandero a GameEvents.
 *
 * La gamificación se DERIVA del log de actividad (bucket `activity_recent`):
 * no añade tablas ni muta el oplog. `applyEvents` es puro y determinista → el
 * mismo feed produce el mismo `PlayerState` (local en cliente; recomputable en
 * servidor para antifraude).
 *
 * Mapea filas de `activity_recent` (entity_type + action + patch) a los eventos
 * de dominio declarados en `config.ts`:
 *   - `order` con status → "paid"           → order.closed
 *   - `register_session` con status → "closed" → shift.reconciled (cuadre)
 */
import { type ActivityRow, parseActivityPatch } from "../activity/format";
import { applyEvents, initialState } from "./engine";
import { comanderoGamificationConfig, COMANDERO_EVENTS } from "./config";
import { dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** `client_ts` (hora local honesta) si existe; si no, `server_ts`. */
function timestampOf(row: Pick<ActivityRow, "client_ts" | "server_ts">): number | null {
  const raw = row.client_ts ?? row.server_ts;
  if (!raw) {
    return null;
  }
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : t;
}

/** ¿El patch registra una transición de `status` a `value`? */
function statusChangedTo(row: Pick<ActivityRow, "patch">, value: string): boolean {
  const patch = parseActivityPatch(row.patch);
  const change = patch.status;
  return Boolean(change) && change?.new === value;
}

/**
 * Traduce el feed de `activity` a eventos de gamificación (orden ascendente por
 * tiempo, como exige el reducer de rachas). Filas irrelevantes se ignoran.
 */
export function deriveGameEvents(rows: readonly ActivityRow[]): GameEvent[] {
  const events: GameEvent[] = [];
  for (const row of rows) {
    const at = timestampOf(row);
    if (at === null) {
      continue;
    }
    const day = dayKeyUTC(at);
    const base = { at, day, meta: { activityId: row.id } };

    if (
      row.entity_type === "order" &&
      row.action === "updated" &&
      statusChangedTo(row, "paid")
    ) {
      events.push({ type: COMANDERO_EVENTS.orderClosed, ...base });
    } else if (
      row.entity_type === "register_session" &&
      row.action === "updated" &&
      statusChangedTo(row, "closed")
    ) {
      events.push({ type: COMANDERO_EVENTS.shiftReconciled, ...base });
    }
  }
  // El feed llega server_ts DESC; el reducer necesita orden cronológico ASC.
  return events.toSorted((a, b) => a.at - b.at);
}

/**
 * Estado de juego derivado del feed de actividad. Puro: mismo feed → mismo
 * estado. Retorna XP/nivel/rachas/badges/quests listos para la UI.
 */
export function deriveGameState(rows: readonly ActivityRow[]): PlayerState {
  const events = deriveGameEvents(rows);
  return applyEvents(initialState(), events, comanderoGamificationConfig).state;
}
